import React, { useEffect, useState } from 'react';
import { ShareIcon, AddToHomeScreenIcon, XIcon } from 'shared-icons';

interface PwaInstallPromptProps {
  platform: 'ios' | 'android' | 'other_desktop';
  onInstall: () => void;
  onClose: () => void;
}

const PwaInstallPrompt: React.FC<PwaInstallPromptProps> = ({ platform, onInstall, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    const iconUrl = "/android-chrome-192x192.png";

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 500); // Delay for animation
        return () => clearTimeout(timer);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300); // Allow animation to finish
    }

    const handleInstall = () => {
        onInstall();
        // The prompt might be dismissed by the user, we close our UI optimistically
        // because the deferredPrompt can only be used once.
        handleClose();
    }
    
    // Android & Desktop (Chrome, Edge)
    const renderInstallablePrompt = () => (
         <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-2xl p-4 max-w-md w-full flex items-center gap-4">
            <div className="flex-shrink-0">
                <img src={iconUrl} alt="TeezSpeed Logo" className="w-14 h-14" />
            </div>
            <div className="flex-grow">
                <h4 className="font-bold text-slate-900 dark:text-white">Install TeezSpeed App</h4>
                <p className="text-sm text-slate-600 dark:text-slate-300">Get a faster, full-screen experience.</p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-3">
                 <button
                    onClick={handleClose}
                    className="text-sm text-gray-500 dark:text-slate-400 hover:underline"
                >
                    Not now
                </button>
                 <button
                    onClick={handleInstall}
                    className="px-4 py-2 bg-cyan-600 text-white text-sm font-semibold rounded-full hover:bg-cyan-500 transition"
                 >
                    Install
                </button>
            </div>
        </div>
    );

    // iOS (Safari)
    const renderIosPrompt = () => (
         <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-2xl p-4 max-w-md w-full relative">
            <button onClick={handleClose} className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-full" aria-label="Dismiss install prompt">
                <XIcon width={20} height={20} />
            </button>
            <div className="flex items-center gap-4">
                 <div className="flex-shrink-0">
                    <img src={iconUrl} alt="TeezSpeed Logo" className="w-14 h-14" />
                </div>
                <div className="flex-grow">
                    <h4 className="font-bold text-slate-900 dark:text-white">Add to Home Screen</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                        For an app-like experience, tap the <ShareIcon className="inline-block mx-0.5" width={16} height={16} /> icon, then find and tap <AddToHomeScreenIcon className="inline-block mx-0.5" width={16} height={16} /> 'Add to Home Screen'.
                    </p>
                </div>
            </div>
        </div>
    );

    return (
        <div className={`fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-50 transition-all duration-500 ease-out transform-gpu ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
           {platform === 'ios' ? renderIosPrompt() : renderInstallablePrompt()}
        </div>
    );
};

export default PwaInstallPrompt;